/**
 * Customer brand tokens (PLAN-FINAL B7, mock-up `et-bar.js` ET.brandTokens). One accent colour in,
 * every brand token out for light and dark, each pushed until it meets WCAG 2.2 AA: 4.5:1 for text
 * (ink, on-fill) and 3:1 for UI edges (dark fill against the surface, the stripe on the dark bar).
 *
 * If the accent is missing, malformed, or can't be brought to AA, we fall back to AnooreHR's own
 * brand (`SELF.defaultAccent`) and report `fallback: true`.
 */
import { SELF } from './config';

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

type RGB = [number, number, number];

export function parseHex(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const m = HEX.exec(value.trim());
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h.replace(/./g, '$&$&');
  return `#${h.toUpperCase()}`;
}

function rgb(hex: string): RGB {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex(c: number[]): string {
  return `#${c
    .map((v) => {
      const x = Math.max(0, Math.min(255, Math.round(v)));
      return (x < 16 ? '0' : '') + x.toString(16);
    })
    .join('')
    .toUpperCase()}`;
}

export function mix(a: string, b: string, t: number): string {
  const x = rgb(a);
  const y = rgb(b);
  return toHex([0, 1, 2].map((k) => x[k] + (y[k] - x[k]) * t));
}

function luminance(hex: string): number {
  const w = [0.2126, 0.7152, 0.0722];
  return rgb(hex)
    .map((v) => {
      const s = v / 255;
      return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    })
    .reduce((sum, v, k) => sum + v * w[k], 0);
}

export function contrast(a: string, b: string): number {
  const l1 = luminance(a);
  const l2 = luminance(b);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

/** Push `c` toward `to` until it reaches `ratio` against every surface in `on`. */
function reach(c: string, to: string, on: string[], ratio: number): string {
  let out = c;
  for (let n = 0; n < 40 && on.some((s) => contrast(out, s) < ratio); n++) out = mix(out, to, 0.06);
  return out;
}

export interface BrandSet {
  brand: string;
  ink: string;
  fill: string;
  on: string;
  soft: string;
  line: string;
}

export interface BrandTokens {
  accent: string;
  light: BrandSet;
  dark: BrandSet;
  /** True when the customer's accent was unusable and AnooreHR's own brand is used instead. */
  fallback: boolean;
}

/** Surfaces the tokens must stand on (et-foundation.css + AnooreHR's room). */
export const SURFACES = {
  light: { surface: '#FFFFFF', s2: '#F9FAFB', bg: '#F5F6FA' },
  dark: { surface: '#161B26', bg: '#0C111D', s2: '#1C222E', bar: '#10141D' },
} as const;

function derive(accent: string): Omit<BrandTokens, 'fallback'> {
  const L = SURFACES.light;
  const D = SURFACES.dark;
  const light: BrandSet = {
    brand: accent,
    ink: reach(accent, '#000000', [L.surface, L.s2, L.bg, mix(accent, '#FFFFFF', 0.9)], 4.5),
    fill: accent,
    on: '#FFFFFF',
    soft: mix(accent, '#FFFFFF', 0.9),
    line: mix(accent, '#FFFFFF', 0.68),
  };
  light.on =
    contrast(accent, '#FFFFFF') >= 4.5 ? '#FFFFFF' : contrast(accent, '#101828') >= 4.5 ? '#101828' : '#FFFFFF';
  if (contrast(light.fill, light.on) < 4.5) light.fill = reach(accent, '#000000', ['#FFFFFF'], 4.5);

  const dark: BrandSet = {
    brand: reach(accent, '#FFFFFF', [D.bar], 3),
    ink: reach(accent, '#FFFFFF', [D.surface, D.bg, D.s2, mix(accent, D.surface, 0.8)], 4.5),
    fill: reach(accent, '#FFFFFF', [D.surface], 3),
    on: '#FFFFFF',
    soft: mix(accent, D.surface, 0.8),
    line: mix(accent, D.surface, 0.5),
  };
  dark.on = contrast(dark.fill, '#FFFFFF') >= 4.5 ? '#FFFFFF' : '#0C111D';
  if (contrast(dark.fill, dark.on) < 4.5) {
    dark.fill = reach(dark.fill, '#FFFFFF', [D.surface], 7);
    dark.on = '#0C111D';
  }
  return { accent, light, dark };
}

/** Every AA pair the tokens promise. */
export function passesAA(t: Omit<BrandTokens, 'fallback'>): boolean {
  const L = SURFACES.light;
  const D = SURFACES.dark;
  return (
    [L.surface, L.s2, L.bg, t.light.soft].every((s) => contrast(t.light.ink, s) >= 4.5) &&
    contrast(t.light.fill, t.light.on) >= 4.5 &&
    [D.surface, D.bg, D.s2, t.dark.soft].every((s) => contrast(t.dark.ink, s) >= 4.5) &&
    contrast(t.dark.fill, t.dark.on) >= 4.5 &&
    contrast(t.dark.fill, D.surface) >= 3 &&
    contrast(t.dark.brand, D.bar) >= 3
  );
}

export function brandTokens(accent: string | null | undefined): BrandTokens {
  const parsed = parseHex(accent);
  if (parsed) {
    const t = derive(parsed);
    if (passesAA(t)) return { ...t, fallback: false };
  }
  return { ...derive(SELF.defaultAccent), fallback: true };
}

/** CSS custom properties for one mode, as React inline style. */
export function brandCssVars(set: BrandSet): Record<string, string> {
  return {
    '--et-brand': set.brand,
    '--et-brand-ink': set.ink,
    '--et-brand-fill': set.fill,
    '--et-on-brand': set.on,
    '--et-brand-soft': set.soft,
    '--et-brand-line': set.line,
  };
}
